import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { CommandPalette } from '@/components/command/CommandPalette';
import { UserProfileDropdown } from './UserProfileDropdown';
import { track } from '@/lib/telemetry';
import { useUnreadNotifications } from '@/hooks/useUnreadNotifications';
import { useUIStore } from '@/stores/uiStore';
import { Bell, PanelLeft, Settings2 } from 'lucide-react';
import { formatModKChord } from '@/utils/modKeyLabel';
import { isPlatformAdmin } from '@/utils/access';
import { useAuth } from '@/state/AuthContext';
import { useAdminHomeCustomizeStore } from '@/stores/adminHomeCustomizeStore';

export function Header() {
  const [cmdkMounted, setCmdkMounted] = useState(false);
  const { unreadCount } = useUnreadNotifications();
  const nav = useNavigate();
  const { pathname } = useLocation();
  const { user } = useAuth();
  const openCustomizeHome = useAdminHomeCustomizeStore((st) => st.open);
  // The gear customizes the page it sits on, so it exists only on Admin Home.
  const showCustomizeHome = pathname === '/home' && isPlatformAdmin(user);
  const sidebarOpen = useUIStore((s) => s.sidebarOpen);
  const toggleSidebar = useUIStore((s) => s.toggleSidebar);
  const modK = formatModKChord();

  useEffect(() => setCmdkMounted(true), []);

  const openCmdK = () => {
    const ke = new KeyboardEvent('keydown', { key: 'k', ctrlKey: true });
    window.dispatchEvent(ke);
    track('ui.menu.open', { surface: 'cmdk' });
  };

  return (
    <header
      data-testid="app-header"
      className="relative z-50 h-14 shrink-0 border-b border-slate-200/80 bg-white/95 backdrop-blur-sm flex items-center gap-4 px-4 lg:px-6 dark:border-slate-700/80 dark:bg-slate-900/95"
    >
      {/* Mobile: toggle navigation drawer (desktop uses in-flow sidebar + PanelLeft reopen). */}
      <button
        type="button"
        data-testid="sidebar-mobile-toggle"
        aria-label={sidebarOpen ? 'Close sidebar' : 'Open sidebar'}
        aria-expanded={sidebarOpen}
        title={sidebarOpen ? 'Close sidebar' : 'Open sidebar'}
        onClick={() => toggleSidebar()}
        className="md:hidden rounded-lg p-2 text-slate-500 hover:bg-slate-100 hover:text-slate-800 dark:hover:bg-slate-800 dark:hover:text-slate-200"
      >
        <PanelLeft className="h-5 w-5" />
      </button>

      <div className="flex-1" />

      {/* Center: search trigger */}
      <button
        data-testid="cmdk-button"
        aria-label="Open Command Palette"
        onClick={openCmdK}
        className="hidden w-full max-w-xl md:flex items-center gap-3 rounded-xl border border-slate-200/80 bg-white px-4 py-2 text-sm text-slate-500 shadow-sm hover:border-slate-300 hover:shadow transition dark:border-slate-600/80 dark:bg-slate-800 dark:text-slate-300 dark:hover:border-slate-500"
      >
        <span className="text-slate-400">&#x2315;</span>
        <span className="flex-1 text-left">Search workspaces, projects, tasks, dashboards</span>
        <kbd
          className="rounded-md border border-slate-200 bg-slate-50 px-2 py-0.5 text-[11px] text-slate-400 dark:border-slate-600 dark:bg-slate-700/80 dark:text-slate-400"
          data-testid="cmdk-mod-hint"
        >
          {modK}
        </kbd>
      </button>

      {/* Mobile: compact search button */}
        <button
          data-testid="cmdk-button-mobile"
          aria-label="Open Command Palette"
          title="Open command palette"
          onClick={openCmdK}
          className="md:hidden rounded-lg p-2 hover:bg-slate-100 dark:hover:bg-slate-800"
        >
        <span className="text-sm text-slate-500" data-testid="cmdk-mod-hint-mobile">{modK}</span>
      </button>

      <div className="flex flex-1 items-center justify-end gap-3">
        {showCustomizeHome && (
          <button
            data-testid="header-customize-home"
            aria-label="Customize home"
            title="Customize home"
            onClick={openCustomizeHome}
            className="rounded-lg p-2 hover:bg-slate-100 transition dark:hover:bg-slate-800"
          >
            <Settings2 className="h-5 w-5 text-slate-500" />
          </button>
        )}

        {/* Notification bell */}
        <button
          data-testid="header-notifications"
          aria-label={unreadCount > 0 ? `Notifications, ${unreadCount} unread` : 'Notifications'}
          title="Notifications"
          onClick={() => nav('/inbox')}
          className="relative rounded-lg p-2 hover:bg-slate-100 transition dark:hover:bg-slate-800"
        >
          <Bell className="h-5 w-5 text-slate-500" />
          {unreadCount > 0 && (
            <span
              data-testid="header-unread-count"
              className="absolute -right-0.5 -top-0.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-red-600 px-1 text-[10px] font-semibold leading-none text-white"
            >
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </button>

        {/* Profile avatar — no divider, avatar-only per locked spec */}
        <UserProfileDropdown align="right" />
      </div>

      {cmdkMounted && <CommandPalette />}
    </header>
  );
}
